import { PropsWithChildren, useCallback } from 'react';

import { AnimatePresence, motion } from 'framer-motion';

import { x } from '@/style';

import { SurveyType } from '@graphql/requests';

import { useAnalyticsReportingService } from '@ui-furniture/common/analytics';
import { Price } from '@ui-furniture/common/price';
import { Button, Card, Hr, HStack, Text, VStack } from '@ui-furniture/elements';
import {
  CardSubtitle,
  CardTitle,
} from '@ui-furniture/elements/card/components';
import { useStateModalControls } from '@ui-furniture/lib/modal';
import { ChildAnimation } from '@ui-furniture/ui/animation/child';
import { Chip } from '@ui-furniture/ui/chip';
import { LineIcon } from '@ui-furniture/ui/line-icon';

import { BookGeneralSurveyModal } from './book-general-survey-modal';

import { useSurveyRecommendation } from '../context/survey-recommendation';

const RowItem = ({
  isIncluded,
  showText,
  children,
}: PropsWithChildren<{
  isIncluded: boolean;
  showText?: boolean;
}>) => (
  <HStack opacity={isIncluded ? 1 : 0.4} alignItems='flex-start' gap='xs'>
    <x.div position='relative' top={showText ? '7px' : 'unset'}>
      {isIncluded ? (
        <LineIcon size='16px' name='check' />
      ) : showText ? (
        <LineIcon size='16px' name='close' />
      ) : null}
    </x.div>
    {showText ? (
      <Text fontSize='sm' my='sm' lineHeight='1.4' position='relative'>
        {children}
      </Text>
    ) : null}
  </HStack>
);

export const GeneralSurveyCard = ({
  name,
  level,
  summary,
  type,
  includedItems,
  isDisabled,
  showText,
}: {
  name: string;
  level: 1 | 2 | 3;
  summary: string;
  type: SurveyType | null;
  explanation: string;
  includedItems: Array<string>;
  isDisabled?: boolean;
  showText?: boolean;
}) => {
  const { reportObjective } = useAnalyticsReportingService();
  const { recommendation, isFormDirty, ensureFormIsComplete } =
    useSurveyRecommendation();
  const price = recommendation?.surveyPrices?.[level];
  const isRecommended = recommendation?.recommendedSurvey === level;
  const modalControls = useStateModalControls({
    onOpen: () => {
      reportObjective({
        category: 'Survey',
        stage: 'event',
        name: 'Survey - Open Book Survey Modal',
        data: {
          name,
          surveyType: type,
          isRecommended,
          level,
          price,
        },
      });
    },
  });
  const requestBookSurvey = useCallback(async () => {
    const isFormComplete = await ensureFormIsComplete();
    if (isFormComplete) modalControls.open();
  }, [recommendation, ensureFormIsComplete]);

  return (
    <>
      {level !== 1 ? (
        <BookGeneralSurveyModal controls={modalControls} level={level} />
      ) : null}
      <x.div
        position='relative'
        display='grid'
        gridTemplateRows='subgrid'
        gridRow={'1 / -1'}
        {...(isDisabled
          ? {
              opacity: 0.6,
              pointerEvents: 'none',
            }
          : {})}
      >
        <Card
          variant={isRecommended && !isFormDirty ? 'highlight' : 'regular'}
          display='grid'
          gridTemplateRows='subgrid'
          gridRow='1 / -1'
          p='md'
          textAlign={'center'}
        >
          <AnimatePresence>
            {isRecommended && !isFormDirty ? (
              <x.div
                as={motion.div}
                variants={{
                  hide: {
                    opacity: 0,
                  },
                  show: {
                    opacity: 1,
                  },
                }}
                h='28px'
                initial='hide'
                animate='show'
                exit='hide'
                position='absolute'
                top='0'
                justifySelf='center'
              >
                <Chip variant='highlight' transform='translateY(-50%)'>
                  Recommended
                </Chip>
              </x.div>
            ) : null}
          </AnimatePresence>
          <VStack>
            <CardTitle mt='md'>RICS Level {level}</CardTitle>
          </VStack>
          <CardSubtitle mb='md'>{name}</CardSubtitle>
          <Text variant='bodySmall'>{summary}</Text>
          {price && !isFormDirty ? (
            <ChildAnimation>
              <Price price={price} />
            </ChildAnimation>
          ) : (
            <x.div my='md' />
          )}
          {!isDisabled ? (
            <Button
              type='button'
              variant='outline'
              version='secondary'
              onClick={requestBookSurvey}
            >
              Book Now
            </Button>
          ) : (
            <x.div />
          )}
          <Hr />

          <x.div
            display='grid'
            gridTemplateRows='subgrid'
            style={{ gridRowEnd: 'span 15' }}
            alignItems='center'
            textAlign='left'
            justifyContent={showText ? 'flex-start' : 'center'}
            rowGap='md'
          >
            <RowItem
              showText={showText}
              isIncluded={includedItems.indexOf('overall') !== -1}
            >
              Assessment of Overall Condition
            </RowItem>
            <RowItem
              showText={showText}
              isIncluded={includedItems.indexOf('roof') !== -1}
            >
              Roof
            </RowItem>
            <RowItem
              showText={showText}
              isIncluded={includedItems.indexOf('walls') !== -1}
            >
              Walls
            </RowItem>
            <RowItem
              showText={showText}
              isIncluded={includedItems.indexOf('windows') !== -1}
            >
              Windows
            </RowItem>
            <RowItem
              showText={showText}
              isIncluded={includedItems.indexOf('doors') !== -1}
            >
              Doors
            </RowItem>
            <RowItem
              showText={showText}
              isIncluded={includedItems.indexOf('structural') !== -1}
            >
              Structural Integrity
            </RowItem>
            <RowItem
              showText={showText}
              isIncluded={includedItems.indexOf('visible-defects') !== -1}
            >
              Visible Defects
            </RowItem>
            <RowItem
              showText={showText}
              isIncluded={includedItems.indexOf('recommendation') !== -1}
            >
              Recommendations of Repairs
            </RowItem>
            <RowItem
              showText={showText}
              isIncluded={includedItems.indexOf('invisible-defects') !== -1}
            >
              Not Immediately Visible Defects
            </RowItem>
            <RowItem
              showText={showText}
              isIncluded={includedItems.indexOf('secondary-signs-damp') !== -1}
            >
              Secondary Signs of Damp, Rot, Infestation
            </RowItem>
            <RowItem
              showText={showText}
              isIncluded={includedItems.indexOf('materials') !== -1}
            >
              Establish Construction Materials/Methods
            </RowItem>
            <RowItem
              showText={showText}
              isIncluded={includedItems.indexOf('repair-timeline') !== -1}
            >
              Repair Timeline and Consequences of Inaction
            </RowItem>
            <RowItem
              showText={showText}
              isIncluded={includedItems.indexOf('cost-estimation') !== -1}
            >
              Estimation of Costs For Repairs
            </RowItem>
            <RowItem
              showText={showText}
              isIncluded={includedItems.indexOf('energy-efficiency') !== -1}
            >
              Specific Comments on Energy Efficiency
            </RowItem>
          </x.div>
        </Card>
      </x.div>
    </>
  );
};
